'use client';

import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  BookingBookabilityReason,
  BookingBookabilityReasonTranslation,
  TRANSLATE_KEY_BOOKING_BOOKABILITY_REASON,
  computeBillableUnits,
  formatDurationFromMinutes,
  formatPriceOptionsRange,
  isAvailablePriceOption,
  isBookablePriceOption,
  isNotBookableOnline,
  isPartySizeSelectable,
  resolvePartySizeMax,
} from '@cfixe-monorepo/shared';
import type { BookingPrestation, CartItem, PriceOption, SelectedSupplement } from '../../types';
import { promotionBadgeLabel, promotionCardPrice } from '../../utils/promotion-display';
import { hasChoicesBeyondPrice } from '../../utils/prestation-choices';
import { useBooking } from '../../context/BookingContext';
import { resolveServiceCardState } from '../../utils/service-card-state';
import { useOverlayPortalTarget } from '../../utils/overlay-portal';
import { slotWindowEnd, toISODate } from '../../utils/date-helpers';
import { CallProButton } from './CallProButton';
import { PrestationConfigurator } from './PrestationConfigurator';
import { PartySizeSelector } from './PartySizeSelector';
import { ImageGallery } from './ImageGallery';
import { RichDescription } from './RichDescription';
import VideoPlayer from '../../../website/components/shared/VideoPlayer';

/* Phrase client d'une raison de non-réservabilité (spec Parcours § 7.8). */
const bookabilityReasonLabel = (reason: BookingBookabilityReason): string | undefined =>
  BookingBookabilityReasonTranslation['fr-FR'][`${TRANSLATE_KEY_BOOKING_BOOKABILITY_REASON}${reason}`];

/* Injecte les noms, entre guillemets français, dans un libellé de remplacement. */
const fillReplaceLabel = (label: string, name: string, cartNames: string[]): string =>
  label.replace('{name}', `« ${name} »`).replace('{names}', cartNames.map((n): string => `« ${n} »`).join(', '));

interface ServiceCardProps {
  prestation: BookingPrestation;
  isExpanded: boolean;
  onToggle: () => void;
  onAdd: (item: CartItem) => void;
  onReplace: (item: CartItem) => void;
}

export function ServiceCard({ prestation: livePrestation, isExpanded, onToggle, onAdd, onReplace }: ServiceCardProps) {
  const { formatPrice, labels, params, ctx, send, callbacks } = useBooking();
  const singleSelection = params.allow_multi_prestation === false;
  /* Multi-prestation OFF : la carte de la prestation DÉJÀ sélectionnée ne propose pas de « se
     remplacer elle-même » — son CTA met à jour la sélection et passe à l'étape suivante. */
  const selectedCartItem = singleSelection
    ? ctx.cartItems.find((i): boolean => i.prestation.prestation_id === livePrestation.prestation_id)
    : undefined;
  const inSelection = ctx.cartItems.some((i): boolean => i.prestation.prestation_id === livePrestation.prestation_id);
  /* Le panier ne bloque jamais une carte : une prestation réservable seule mais pas avec la
     sélection courante (créneau ou lieu), ou toute autre prestation en multi-prestation OFF,
     propose de REMPLACER la sélection et dit pourquoi. Seule une indisponibilité réelle grise la
     carte. En conflit, la carte se configure sur la version sondée SANS panier (`prestation`). */
  const {
    source: prestation,
    actionable: isBookable,
    replaceReason,
  } = resolveServiceCardState({
    prestation: livePrestation,
    baseline: ctx.baselinePrestations.find((p): boolean => p.prestation_id === livePrestation.prestation_id),
    cartItems: ctx.cartItems,
    placeIds: ctx.availablePlaces.map((p): number => p.pro_place_id),
    singleSelection,
  });
  const replaceMode = replaceReason !== undefined;
  /* Raison d'indisponibilité connue du serveur (sans panier en conflit) : une phrase et une sortie. */
  const reason = !isBookable && !replaceMode ? livePrestation.bookability_reason : undefined;
  const reasonLabel = reason ? bookabilityReasonLabel(reason) : undefined;
  const canRequestQuote =
    livePrestation.bookability_reason === BookingBookabilityReason.QUOTE_ONLY && !!callbacks.onSubmitQuoteRequest;
  /* Délai minimum du pro : réservable, mais pas avant la première semaine de la grille. */
  const bookableFrom =
    isBookable &&
    livePrestation.first_bookable_date_formatted &&
    livePrestation.first_bookable_date &&
    livePrestation.first_bookable_date > slotWindowEnd(toISODate(new Date()))
      ? labels.bookable_from.replace('{date}', livePrestation.first_bookable_date_formatted)
      : undefined;
  const cartNames = [...new Set(ctx.cartItems.map((i): string => i.prestation.name))];
  const replaceNote = replaceReason
    ? fillReplaceLabel(
        {
          single: labels.replace_reason_single,
          slot: labels.replace_reason_slot,
          place: labels.replace_reason_place,
        }[replaceReason],
        livePrestation.name,
        cartNames,
      )
    : undefined;
  /* L'action nomme ce qui part et ce qui arrive ; avec plusieurs prestations, « ma sélection ». */
  const replaceActionLabel = fillReplaceLabel(
    cartNames.length === 1 ? labels.replace_item : labels.replace_selection,
    livePrestation.name,
    cartNames,
  );
  /* DEUX listes, à ne pas confondre — c'est ce qui a produit deux des défauts de la relecture.

     `bookablePrices` = ce qu'on AFFICHE dans le sélecteur : toute ligne portant une durée et non
     « sur devis ». Les formules sans créneau y restent, GRISÉES (cf. DurationSelector) — même
     doctrine que les lieux, les membres et les jours du calendrier. Les retirer créait deux listes
     divergentes : l'en-tête pouvait annoncer un tarif absent du sélecteur.

     `availablePrices` (dans resolveServiceCardState) = ce qui est SÉLECTIONNABLE, donc ce qui
     décide de l'état de la carte. Sans lui, une prestation annoncée réservable dont toutes les
     formules sont indisponibles (le recalcul live peut renvoyer un verdict prestation sans verdict
     par durée, cf. la borne multi-lieux) donnerait un bouton actif et muet. */
  const bookablePrices = prestation.prices.filter(isBookablePriceOption);

  /* Index de la première ligne PROPOSABLE : sert de sélection par défaut pour qu'une prestation
     « mixte » (lignes avec durée + lignes vitrine sans durée) ne démarre jamais sur une ligne
     0-min non réservable, ni sur une durée que le backend a sondée sans trouver de créneau.
     -1 → aucune ligne proposable (prestation purement vitrine, ou toutes durées complètes). */
  const defaultBookableIndex = prestation.prices.findIndex(isAvailablePriceOption);
  const [selectedPriceIndex, setSelectedPriceIndex] = useState(defaultBookableIndex >= 0 ? defaultBookableIndex : 0);
  const [supplements, setSupplements] = useState<SelectedSupplement[]>((): SelectedSupplement[] =>
    (prestation.supplements ?? []).map((s): SelectedSupplement => ({ ...s, selected: false })),
  );
  const [quantity, setQuantity] = useState(1);
  const [videoOpen, setVideoOpen] = useState(false);
  const cardRef = useRef<HTMLDivElement | null>(null);
  const videoPortalTarget = useOverlayPortalTarget(cardRef, videoOpen);
  const videos = prestation.videos ?? [];
  const hasVideos = videos.length > 0;

  /* Bornes partagées avec la fiche prestation des sites vitrines (@cfixe/shared) : les deux
     surfaces doivent proposer EXACTEMENT le même choix, sinon le panier d'un site pousse dans le
     tunnel une taille de groupe qu'il aurait lui-même refusée. */
  const partySizeUpperBound = resolvePartySizeMax(prestation);
  const partySizeOpen = isPartySizeSelectable(prestation);
  /* Toujours pré-rempli à 1 : `clients_per_booking_max` est un plafond, pas la taille attendue
     du groupe — c'est au client d'augmenter s'il vient à plusieurs. */
  const [partySize, setPartySize] = useState<number>(1);
  /* Unités facturées de la ligne : quantité proposée par défaut à chaque supplément qui se compte
     au choix. Tant que le client n'a pas touché à son stepper, il suit ces unités. */
  const supplementDefaultQuantity = computeBillableUnits({
    quantity,
    party_size: partySizeOpen ? partySize : undefined,
  });

  /* Une prestation dont toutes les durées sont complètes reste une carte de RÉSERVATION grisée
     « Aucune disponibilité » ; seule une prestation 100 % vitrine bascule dans le rendu sans CTA.
     C'est aussi ce qui met les prestations vitrine à l'abri du terme « au moins une formule
     sélectionnable » de `isBookable` : elles n'atteignent jamais ces branches. */
  const hasConfiguredDuration = bookablePrices.length > 0;
  /* Rien à choisir (une seule formule, ni supplément, ni personnes, ni quantité) : « Choisir » sélectionne
     tout de suite, sans déplier la carte (bug 32). Un remplacement pour incompatibilité de lieu ou de
     créneau garde la carte dépliée, qui explique ce qui part. */
  const hasChoices = bookablePrices.length > 1 || hasChoicesBeyondPrice(prestation);
  const directPick = !hasChoices && (replaceReason === undefined || replaceReason === 'single');
  /* Sélection courante : toujours une ligne réservable (jamais une ligne vitrine). */
  const selectedPrice =
    prestation.prices[selectedPriceIndex] ?? prestation.prices[defaultBookableIndex] ?? prestation.prices[0];

  /* Recalage sur une ligne proposable : le recalcul live de bookability peut masquer la durée
     actuellement sélectionnée (le panier a grossi entre-temps). Sans ça, la carte resterait figée
     sur une durée absente du sélecteur et l'ajout au panier enverrait une durée sans créneau. */
  useEffect((): void => {
    const current = prestation.prices[selectedPriceIndex];
    if (current && isAvailablePriceOption(current)) return;
    setSelectedPriceIndex(defaultBookableIndex >= 0 ? defaultBookableIndex : 0);
  }, [prestation.prices, selectedPriceIndex, defaultBookableIndex]);

  const handlePriceChange = (price: PriceOption): void => {
    // Inclure le `label` dans le match : deux lignes peuvent partager durée + prix et ne se
    // distinguer que par leur libellé (ex. « Barbe simple » vs « Barbe + contours »). Sans ça,
    // findIndex renvoie toujours la première et le client réserve/snapshote la mauvaise ligne.
    const idx = prestation.prices.findIndex(
      (p): boolean =>
        p.duration_minutes === price.duration_minutes &&
        p.price === price.price &&
        (p.label ?? '') === (price.label ?? ''),
    );
    setSelectedPriceIndex(idx >= 0 ? idx : 0);
  };

  /* Libellé de prix de la prestation : « Sur devis » (QUOTE), « À partir de X€ » (FROM ou plusieurs
     options, montant ferme le moins cher), montant ferme sinon. Logique partagée avec les listings
     du site public (formatPriceOptionsRange) — même rendu des deux côtés. */
  const displayPrice =
    formatPriceOptionsRange(prestation.prices, labels, (o): string => o.price_string ?? formatPrice(o.price)) ?? null;
  /* Promotion automatique : badge (« −20 % », « −15 % à certaines heures ») ; remise acquise : le prix barré
     puis le prix remisé, chiffrés par le serveur sur la formule affichée. */
  const promotion = prestation.promotion;
  const promotionPrice = promotionCardPrice(promotion, displayPrice);

  /* Display duration: built only from lines that actually carry a duration. Les lignes « vitrine »
     (sans durée) ne contribuent pas — et si aucune n'en a, rien n'est affiché. Min/max RÉELS (pas
     premier/dernier) : les lignes sont triées par `sort_order`, pas par durée. */
  /* Plage de durées de l'en-tête : sur les lignes AFFICHÉES, donc la même liste que le prix
     juste au-dessus. C'est ce qui rend l'en-tête honnête — le prix « à partir de » ne peut plus
     désigner une formule absente de la fourchette annoncée. */
  const bookableDurations = bookablePrices.map((p): number => p.duration_minutes ?? 0);
  const minDuration = bookableDurations.length ? Math.min(...bookableDurations) : 0;
  const maxDuration = bookableDurations.length ? Math.max(...bookableDurations) : 0;
  const displayDuration =
    bookableDurations.length === 0
      ? null
      : minDuration === maxDuration
        ? formatDurationFromMinutes(minDuration)
        : `${formatDurationFromMinutes(minDuration)}-${formatDurationFromMinutes(maxDuration)}`;

  const handleAdd = (): void => {
    /* `isAvailablePriceOption` en plus du garde prestation : entre un refresh live qui masque la
       durée courante et le recalage ci-dessus, un clic ne doit pas glisser une durée sans créneau
       dans le panier. */
    if (!isBookable || !isAvailablePriceOption(selectedPrice)) return;
    (replaceMode ? onReplace : onAdd)({
      prestation,
      selectedPrice,
      selectedPriceIndex,
      supplements,
      quantity,
      party_size: partySizeOpen ? partySize : undefined,
    });
    /* Reset local state for next add */
    setSelectedPriceIndex(defaultBookableIndex >= 0 ? defaultBookableIndex : 0);
    setSupplements((prestation.supplements ?? []).map((s): SelectedSupplement => ({ ...s, selected: false })));
    setQuantity(1);
    setPartySize(1);
    /* Une seule prestation par réservation : le choix fait passer à l'étape suivante. */
    if (singleSelection) send({ type: 'CONFIRM_SERVICES' });
  };

  const handleDirectPick = (event: React.MouseEvent): void => {
    event.stopPropagation();
    handleAdd();
  };

  /* « Retirer » : toutes les lignes de cette prestation, de la dernière à la première (les index suivants
     ne bougent pas). */
  const handleRemove = (event: React.MouseEvent): void => {
    event.stopPropagation();
    ctx.cartItems
      .map((item, index): number => (item.prestation.prestation_id === livePrestation.prestation_id ? index : -1))
      .filter((index): boolean => index >= 0)
      .reverse()
      .forEach((index): void => send({ type: 'REMOVE_FROM_CART', index }));
  };

  /* Réouverture de la prestation déjà sélectionnée : le configurateur reprend la formule, les
     suppléments et les quantités du panier — sinon « Continuer » écraserait silencieusement le
     choix du client par les valeurs par défaut. */
  const handleToggle = (): void => {
    if (!isExpanded && selectedCartItem) {
      setSelectedPriceIndex(selectedCartItem.selectedPriceIndex);
      setSupplements(selectedCartItem.supplements);
      setQuantity(selectedCartItem.quantity);
      setPartySize(selectedCartItem.party_size ?? 1);
    }
    onToggle();
  };

  return (
    <div
      ref={cardRef}
      className={`bt-service-card ${isExpanded ? 'bt-service-card--expanded' : ''} ${
        !isBookable ? 'bt-service-card--unavailable' : ''
      } ${inSelection ? 'bt-service-card--in-selection' : ''}`}
    >
      <div className="bt-service-card__header" onClick={hasConfiguredDuration ? handleToggle : undefined}>
        {prestation.pictures && prestation.pictures.length > 0 && (
          <ImageGallery
            pictures={prestation.pictures}
            alt={prestation.name}
            labels={{ close: labels.close, previous: labels.photo_previous, next: labels.photo_next }}
          />
        )}
        <div className="bt-service-card__info">
          <p className="bt-service-card__name">{prestation.name}</p>
          <div className="bt-service-card__meta">
            {displayDuration && <span className="bt-service-card__duration">{displayDuration}</span>}
            {displayDuration && displayPrice && <span className="bt-service-card__sep">·</span>}
            {promotionPrice ? (
              <>
                <s className="bt-service-card__price bt-service-card__price--before">{promotionPrice.before}</s>
                <span className="bt-service-card__price">{promotionPrice.after}</span>
              </>
            ) : (
              displayPrice && <span className="bt-service-card__price">{displayPrice}</span>
            )}
            {promotion && <span className="bt-service-card__promotion">{promotionBadgeLabel(promotion, labels)}</span>}
          </div>
          {replaceReason === 'slot' || replaceReason === 'place' ? (
            <span className="bt-service-card__hint">
              {replaceReason === 'slot' ? labels.replace_hint_slot : labels.replace_hint_place}
            </span>
          ) : null}
          {bookableFrom ? <span className="bt-service-card__hint">{bookableFrom}</span> : null}
          {isNotBookableOnline(reason) ? (
            <div className="bt-service-card__exits" onClick={(e): void => e.stopPropagation()}>
              <CallProButton />
            </div>
          ) : null}
          {hasVideos && (
            <button
              type="button"
              className="bt-service-card__video-link"
              onClick={(e): void => {
                e.stopPropagation();
                setVideoOpen(true);
              }}
            >
              ▶ {labels.watch_video}
            </button>
          )}
        </div>
        {!isExpanded &&
          hasConfiguredDuration &&
          (inSelection ? (
            /* Déjà choisie : jamais « Choisir » à nouveau ; « Retirer » toujours visible (bug 31). */
            <div className="bt-service-card__chosen-actions">
              <span className="bt-service-card__chosen">
                <svg viewBox="0 0 24 24" width="14" height="14" aria-hidden="true" focusable="false">
                  <path
                    d="M5 12l5 5L20 7"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="3"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
                {labels.chosen}
              </span>
              <button
                type="button"
                className="bt-service-card__remove"
                aria-label={labels.remove_from_selection.replace('{name}', prestation.name)}
                onClick={handleRemove}
              >
                <span aria-hidden="true">×</span>
              </button>
            </div>
          ) : isBookable ? (
            <button
              type="button"
              className={`bt-cta bt-cta--small ${replaceMode ? 'bt-cta--secondary' : ''}`}
              onClick={directPick ? handleDirectPick : undefined}
            >
              {replaceMode ? labels.replace_short : ctx.entryMode === 'multi' ? labels.add_to_selection : labels.choose}
            </button>
          ) : reason === BookingBookabilityReason.NO_SLOT_IN_HORIZON ? (
            <div className="bt-service-card__chosen-actions">
              <span className="bt-service-card__unavailable">{reasonLabel}</span>
              <button type="button" className="bt-link-subtle" aria-expanded={false}>
                {labels.see_options}
              </button>
            </div>
          ) : (
            <span className="bt-service-card__unavailable">{reasonLabel ?? labels.unavailable_no_slot}</span>
          ))}
        {/* Sur devis : la carte reste visible avec sa demande intégrée au tunnel (spec Parcours § 7.8). */}
        {!hasConfiguredDuration && canRequestQuote ? (
          <button
            type="button"
            className="bt-cta bt-cta--small bt-cta--secondary"
            onClick={(event): void => {
              event.stopPropagation();
              send({ type: 'REQUEST_QUOTE', prestationId: livePrestation.prestation_id });
            }}
          >
            {labels.request_quote}
          </button>
        ) : null}
      </div>

      {isExpanded && hasConfiguredDuration && (
        <div className="bt-service-card__body">
          {prestation.description && <RichDescription html={prestation.description} labels={labels} />}

          <PrestationConfigurator
            prices={bookablePrices}
            selectedPrice={selectedPrice}
            onPriceChange={handlePriceChange}
            supplements={supplements}
            onSupplementsChange={setSupplements}
            supplementDefaultQuantity={supplementDefaultQuantity}
            quantity={quantity}
            onQuantityChange={setQuantity}
            showQuantity={(prestation.max_quantity ?? 1) > 1}
            maxQuantity={prestation.max_quantity}
            formatPrice={formatPrice}
            labels={{
              duration_label: labels.duration_label,
              duration_unavailable: labels.duration_unavailable,
              price_from: labels.price_from,
              on_quote: labels.on_quote,
              free: labels.free,
              supplements_label: labels.supplements_label,
              add_supplement: labels.add_supplement,
              remove_supplement: labels.remove_supplement,
              supplements_show_more_one: labels.supplements_show_more_one,
              supplements_show_more_other: labels.supplements_show_more_other,
              quantity_label: labels.quantity_label,
              decrease_quantity: labels.decrease_quantity,
              increase_quantity: labels.increase_quantity,
            }}
            actionLabel={
              selectedCartItem ? labels.continue : replaceMode ? replaceActionLabel : labels.add_to_selection
            }
            onAction={handleAdd}
            actionDisabled={!isBookable}
            /* Déjà dans la sélection sans rien à choisir : la rouvrir ne sert qu'à lire la description, un
               second ajout ferait un doublon. */
            hideAction={inSelection && !singleSelection && !hasChoices}
            beforeQuantity={
              partySizeOpen ? (
                <PartySizeSelector
                  partySize={partySize}
                  onChange={setPartySize}
                  max={partySizeUpperBound}
                  labels={{
                    party_size_label: labels.party_size_label,
                    party_size_max_hint: labels.party_size_max_hint,
                    party_size_decrease: labels.party_size_decrease,
                    party_size_increase: labels.party_size_increase,
                  }}
                />
              ) : undefined
            }
            afterControls={
              !isBookable ? (
                <div className="bt-service-card__unavailable-block">
                  <p className="bt-service-card__unavailable bt-service-card__unavailable--block">
                    {reason === BookingBookabilityReason.NO_SLOT_IN_HORIZON
                      ? labels.full_for_now_hint
                      : isNotBookableOnline(reason)
                        ? labels.not_bookable_online_hint
                        : labels.unavailable_no_slot}
                  </p>
                  {reason ? <CallProButton /> : null}
                </div>
              ) : replaceNote ? (
                <p className="bt-service-card__replace-note">{replaceNote}</p>
              ) : undefined
            }
          />
        </div>
      )}

      {/* Portal HORS de .bt-root : la racine déclare `container-type: inline-size`
          (layout containment) qui ferait d'elle le containing block du backdrop
          `position: fixed` — voile partiel et centrage cassé sinon. Cible résolue par
          `useOverlayPortalTarget` (<body>, ou la racine du shadow root dans le widget
          embarqué, sinon la modale sortirait de sa feuille de style). Rendu uniquement
          après interaction (videoOpen). */}
      {videoOpen &&
        videoPortalTarget &&
        createPortal(
          <div
            className="bt-video-modal__backdrop"
            role="dialog"
            aria-modal="true"
            aria-label={labels.video_modal_title}
            onClick={(): void => setVideoOpen(false)}
          >
            <div className="bt-video-modal" onClick={(e): void => e.stopPropagation()}>
              <div className="bt-video-modal__header">
                <span className="bt-video-modal__title">{prestation.name}</span>
                <button
                  type="button"
                  className="bt-video-modal__close"
                  aria-label={labels.close}
                  onClick={(): void => setVideoOpen(false)}
                >
                  ✕
                </button>
              </div>
              <div className="bt-video-modal__list">
                {videos.map((video, i) => (
                  <div key={`${video.video_ref}-${i}`} className="bt-video-modal__item">
                    <VideoPlayer videoRef={video.video_ref} title={video.title} />
                    {video.title && <span className="bt-video-modal__caption">{video.title}</span>}
                  </div>
                ))}
              </div>
            </div>
          </div>,
          videoPortalTarget,
        )}
    </div>
  );
}
